/**
 * Vocabulário da operação em português, e o tom de cada estado.
 *
 * Os mapas são `Record<UniãoDoContrato, …>`: se o contrato ganhar um estado
 * novo e ninguém traduzir aqui, o build quebra. É de propósito — enum que
 * escapa para a tela vira "SUSPENSO" cru na cara de quem opera, e pior,
 * vira estado sem cor definida, que o gestor lê como "normal".
 */
import type {
  Etapa,
  EstadoAtividade,
  EstadoNumero,
  NivelMidia,
  Pool,
  TipoAtividade,
} from "@co/contracts";
import type { Tom } from "./base.js";

export const ESTADO_NUMERO: Record<
  EstadoNumero,
  { rotulo: string; tom: Tom; nota: string }
> = {
  novo: { rotulo: "Novo", tom: "idle", nota: "Cadastrado, ainda sem aquecer" },
  aquecendo: { rotulo: "Aquecendo", tom: "cool", nota: "Cumprindo o plano" },
  apto: { rotulo: "Apto", tom: "ok", nota: "Liberado para campanha" },
  suspenso: { rotulo: "Suspenso", tom: "warn", nota: "Fora de uso, sob análise" },
  banido: { rotulo: "Banido", tom: "crit", nota: "Perdido — não volta" },
};

export const ORDEM_ESTADO: readonly EstadoNumero[] = [
  "novo",
  "aquecendo",
  "apto",
  "suspenso",
  "banido",
];

export const POOL: Record<Pool, string> = {
  disparo: "Disparo",
  sdr: "SDR",
};

export const NIVEL_MIDIA: Record<NivelMidia, string> = {
  texto: "Só texto",
  texto_imagem: "Texto e imagem",
  completo: "Mídia completa",
};

export const ESTADO_ATIVIDADE: Record<
  EstadoAtividade,
  { rotulo: string; tom: Tom }
> = {
  programada: { rotulo: "Programada", tom: "idle" },
  na_fila: { rotulo: "Na fila", tom: "cool" },
  executando: { rotulo: "Executando", tom: "ember" },
  concluida: { rotulo: "Concluída", tom: "ok" },
  falhou: { rotulo: "Falhou", tom: "crit" },
  cancelada: { rotulo: "Cancelada", tom: "idle" },
  bloqueada: { rotulo: "Bloqueada por regra", tom: "warn" },
};

export const ORDEM_ATIVIDADE: readonly EstadoAtividade[] = [
  "programada",
  "na_fila",
  "executando",
  "concluida",
  "falhou",
  "bloqueada",
  "cancelada",
];

/** Só estas podem ser canceladas: o que já saiu não volta atrás. */
export const CANCELAVEIS: readonly EstadoAtividade[] = ["programada", "na_fila"];

export const TIPO_ATIVIDADE: Record<TipoAtividade, string> = {
  conexao: "Conexão",
  envio: "Envio",
  recebimento: "Recebimento",
  interacao: "Interação",
  grupo: "Grupo",
  midia: "Mídia",
  chamada: "Chamada",
  repouso: "Repouso",
  verificacao: "Verificação",
};

export const ATIVIDADES: readonly TipoAtividade[] = Object.keys(
  TIPO_ATIVIDADE,
) as TipoAtividade[];

/** O tipo vem como string livre no contrato; sem tradução, mostra-se o bruto. */
export function rotuloAtividade(tipo: string): string {
  return TIPO_ATIVIDADE[tipo as TipoAtividade] ?? tipo;
}

export const ACAO_EM_FALHA: Record<Etapa["acaoEmFalha"], string> = {
  pausar: "Pausar o número",
  quarentena: "Colocar em quarentena",
  repetir_etapa: "Repetir a etapa",
  retornar_etapa: "Voltar uma etapa",
  reprovar: "Reprovar o número",
};

export const ACOES_EM_FALHA: ReadonlyArray<Etapa["acaoEmFalha"]> = Object.keys(
  ACAO_EM_FALHA,
) as Array<Etapa["acaoEmFalha"]>;

export const TOM_ACAO_EM_FALHA: Record<Etapa["acaoEmFalha"], Tom> = {
  pausar: "warn",
  quarentena: "crit",
  repetir_etapa: "cool",
  retornar_etapa: "cool",
  reprovar: "crit",
};

export const DIAS_SEMANA: readonly string[] = [
  "dom",
  "seg",
  "ter",
  "qua",
  "qui",
  "sex",
  "sáb",
];

export const APROVACAO: Record<"automatica" | "manual", string> = {
  automatica: "Automática ao cumprir os critérios",
  manual: "Manual, pelo gestor",
};

/** `null` no ator significa decisão do motor — nunca "usuário desconhecido". */
export const ORIGEM: Record<"manual" | "automatica", { rotulo: string; tom: Tom }> = {
  manual: { rotulo: "Manual", tom: "ember" },
  automatica: { rotulo: "Motor", tom: "idle" },
};
